/**
 * @fileOverview Tracks whether a surface holds edits not saved yet, keeps a local draft of them, and
 * registers the surface for the leave question. See docs/unsaved-changes.md.
 */

import {
  type ComputedRef,
  type InjectionKey,
  type ShallowRef,
  computed,
  inject,
  nextTick,
  onScopeDispose,
  provide,
  shallowRef,
  toRaw,
  watch,
} from "vue"
import {
  type Draft,
  type DraftKey,
  type LeaveQuestionEntry,
  type UnsavedChangesEntry,
  confirmLeave,
  draftStorageKey,
  readDraft,
  registerUnsavedChanges,
  removeDraft,
  removeOwnDraft,
  writeDraft,
} from "@/services/unsavedChanges"
import { IS_VIEW_HELP_EMBED } from "@/services/viewHelp/embed"
import { auth } from "@/services/auth"
import { useAuthStore } from "@/store/authentication"

export interface UnsavedChangesOptions<T> {
  /** The edits as they are on screen. What a draft stores, and what `restore` gets back. */
  state: () => T
  /**
   * The part of `state` a save would send, which is what "changed" is decided on. Leave out what only
   * the screen uses and what the page fills in by itself.
   */
  compare?: (_state: T) => unknown
  /**
   * Input typed but not yet added to the record: a comment still in its field, a shift not confirmed.
   * The save takes it over, so leaving loses it; it counts as a change, but is not part of a draft.
   */
  pending?: () => boolean
  /** How the leave question names it. */
  label: () => { icon: string; name: string }
  /** Where its draft lives, or null for none: a form filled in from elsewhere, a read-only view. */
  draft?: () => DraftKey | null
  /** `lastModifiedAt` of the saved state, to tell whether the record was saved again since a draft. */
  version?: () => string | null | undefined
  /** Puts a draft's edits back on screen. Without it no draft is offered. */
  restore?: (_state: T) => void
  /**
   * What its Speichern button does. An edit component sets it with `useUnsavedChangesSaveButton`. With
   * `savesOnLeave`, it resolves `true` once the save went through.
   */
  submit?: () => unknown
  /** See `LeaveQuestionEntry.savesOnLeave`. */
  savesOnLeave?: boolean
  layer?: UnsavedChangesEntry["layer"]
  isFront?: () => boolean
  reveal?: () => void
  /**
   * Where the record's attachments are in `state`, as a dot path ("files", "customer.files"). An upload
   * or a deletion reaches the server at once, so the Dateien tab hands it to `adoptFiles`; only a
   * file's tags and due date wait for the record's save.
   */
  filesAt?: string
}

export interface UnsavedChanges {
  isDirty: ComputedRef<boolean>
  /** A draft found for this record, until it is restored or dismissed. */
  pendingDraft: ShallowRef<Draft | null>
  /** The draft was made from an older saved state than the one on screen: someone saved since. */
  draftIsOutdated: ComputedRef<boolean>
  /**
   * Takes `saved` (the server's answer) as the saved state, or what is on screen when left out: after
   * loading a record, after saving it. Tracking starts with the first call.
   */
  markSaved: (_saved?: unknown) => void
  /**
   * Resolves once `request` has: `markSaved()` and `true` when it went through, `false` when it was
   * refused (the api interceptor has put that on screen already). For a page's `save`, whose store puts
   * the server's answer on screen before resolving.
   */
  markSavedWhen: (_request: Promise<unknown> | null | undefined) => Promise<boolean>
  /**
   * Takes values the page filled in by itself (a default role, a list re-read from the server) into the
   * saved state, so they are not the user's edits: the fields given, or what `update` makes of a copy of
   * the saved state.
   */
  adopt: (_fields: Record<string, unknown> | ((_saved: unknown) => unknown)) => void
  /** The attachment list as it is on screen is saved: a file was uploaded or deleted just now. */
  adoptFiles: () => void
  /** Stops tracking until the next `markSaved`, while another record loads into the same page. */
  forget: () => void
  /** Throws the edits away as far as tracking goes: clean from here, and no draft. */
  discard: () => void
  /** Takes the latest change into `isDirty` and the draft now instead of a moment later. */
  flush: () => void
  restoreDraft: () => void
  dismissDraft: () => void
  /**
   * Removes this person's draft of another record: one the page has just turned into the record it
   * shows now (a built-in PDF-Vorlage saved as the workspace's copy). Left, it would be offered again
   * there with edits that are saved.
   */
  removeDraftOf: (_key: DraftKey) => void
  /** Sets what the Speichern button does; see `UnsavedChangesOptions.submit`. */
  setSubmit: (_submit: () => unknown) => void
  /**
   * Runs the Speichern button's save, one at a time: a second press, Ctrl+S or the leave question's
   * Speichern while one is on its way gets its answer instead of sending the record again.
   */
  submit: () => Promise<unknown>
  /** A save run by `submit` is on its way. */
  isSaving: () => boolean
  /** What the leave question and Ctrl+S see of this surface. */
  entry: UnsavedChangesEntry
}

const UNSAVED_CHANGES: InjectionKey<UnsavedChanges> = Symbol("lew:unsaved-changes")

/** How long typing has to pause before the change is measured and the draft written. */
const SETTLE_MS = 300

/** What a picked file (a Blob) stands in as: it has no fields to compare, the list says it is there. */
const FILE = "[file]"

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value)

/**
 * The value as a save would see it, for comparing. Null, undefined, "" and empty lists and objects
 * are all "nothing": a form that turns a loaded `null` into `""` on mount has not been edited. A
 * number is compared as its digits, since a number field without `.number` hands back text. Keys are
 * sorted, so a field a component adds later in another order is not a change either.
 */
function normalize(value: unknown, path: WeakSet<object>): unknown {
  if (value === null || value === undefined || value === "") return undefined
  if (typeof value === "function" || typeof value === "symbol") return undefined
  if (typeof value === "number") return Number.isFinite(value) ? String(value) : undefined
  if (typeof value !== "object") return value
  if (value instanceof Date) return value.toISOString()
  if (value instanceof Blob) return FILE
  // A record pointing back at its parent. Once on the path is enough.
  if (path.has(value)) return undefined
  path.add(value)
  let result: unknown
  if (Array.isArray(value)) {
    const items = value.map(item => normalize(item, path) ?? null)
    result = items.length > 0 ? items : undefined
  } else {
    const fields: Record<string, unknown> = {}
    Object.keys(value)
      .sort()
      .forEach(key => {
        const field = normalize((value as Record<string, unknown>)[key], path)
        if (field !== undefined) fields[key] = field
      })
    result = Object.keys(fields).length > 0 ? fields : undefined
  }
  path.delete(value)
  return result
}

const fingerprint = (value: unknown) => JSON.stringify(normalize(toRaw(value), new WeakSet())) ?? ""

/** A plain deep copy, detached from the reactive original, as JSON would make it. Cycles are cut. */
function copy(value: unknown, path: WeakSet<object>): unknown {
  if (typeof value === "function" || typeof value === "symbol") return undefined
  if (value === null || typeof value !== "object") return value
  if (value instanceof Date) return value.toISOString()
  if (value instanceof Blob) return FILE
  if (path.has(value)) return undefined
  path.add(value)
  const result = Array.isArray(value)
    ? value.map(item => copy(item, path) ?? null)
    : Object.fromEntries(
        Object.entries(value)
          .map(([key, field]) => [key, copy(field, path)])
          .filter(([, field]) => field !== undefined)
      )
  path.delete(value)
  return result
}

export const snapshot = <T>(value: T): T => copy(toRaw(value), new WeakSet()) as T

/**
 * Brings a restored draft up to the record as it is saved now: a field the user had not changed in the
 * draft (still equal to the state the draft started from) takes today's value, so a colleague's change
 * in between is kept. Objects of the same record are decided field by field, lists as a whole — the
 * decision the project refresh makes (`services/projectRefresh.ts`), but compared the way changes are,
 * so a form's "" for a loaded null is not taken for an edit.
 */
function mergeUnchanged(draft: Record<string, unknown>, base: Record<string, unknown>, saved: Record<string, unknown>) {
  new Set([...Object.keys(saved), ...Object.keys(draft)]).forEach(key => {
    const mine = draft[key]
    const was = base?.[key]
    const now = saved[key]
    if (isPlainObject(mine) && isPlainObject(was) && isPlainObject(now) && mine.id === was.id && was.id === now.id) {
      mergeUnchanged(mine, was, now)
    } else if (fingerprint(mine) === fingerprint(was)) {
      draft[key] = now
    }
  })
}

/**
 * Registers a surface that edits something and saves it with a button, and tracks its changes against
 * the saved state.
 *
 * Provides itself to the components below: `LewUnsavedChangesStatus` shows the state beside the save
 * button, and the edit component registers what its button does (`useUnsavedChangesSaveButton`).
 */
export function useUnsavedChanges<T>(options: UnsavedChangesOptions<T>): UnsavedChanges {
  const compare = options.compare ?? ((state: T) => state)

  /** The saved state, as a plain copy: what a draft's restore is merged against. */
  let saved: T | null = null
  const savedVersion = shallowRef<string | null>(null)
  const baseline = shallowRef<string | null>(null)
  const current = shallowRef<string | null>(null)
  /** Set by `discard`: input left typed but not added was thrown away with the rest. */
  const discarded = shallowRef(false)
  const pendingDraft = shallowRef<Draft | null>(null)
  /** The storage key the pending draft and the drafts written from now on belong to. */
  let draftKey: string | null = null
  let save = options.submit ?? null
  let running: Promise<unknown> | null = null
  let timer: ReturnType<typeof setTimeout> | undefined

  const isDirty = computed(
    () =>
      baseline.value !== null &&
      current.value !== null &&
      (current.value !== baseline.value || (!discarded.value && Boolean(options.pending?.())))
  )
  const draftIsOutdated = computed(
    () => Boolean(pendingDraft.value?.version) && pendingDraft.value.version !== savedVersion.value
  )

  function measure() {
    current.value = fingerprint(compare(options.state()))
  }

  function writeOrRemoveDraft() {
    // A draft on offer stays as it was until the user has decided on it: writing the edits made since
    // over it would throw away the very thing being offered.
    if (!draftKey || pendingDraft.value) return
    if (isDirty.value) {
      writeDraft(draftKey, {
        savedAt: Date.now(),
        data: snapshot(options.state()),
        base: saved,
        version: savedVersion.value,
      })
    } else {
      removeOwnDraft(draftKey)
    }
  }

  function flush() {
    clearTimeout(timer)
    timer = undefined
    if (baseline.value === null) return
    measure()
    writeOrRemoveDraft()
  }

  watch(
    options.state,
    () => {
      if (baseline.value === null) return
      clearTimeout(timer)
      timer = setTimeout(flush, SETTLE_MS)
    },
    { deep: true }
  )

  function rebase() {
    clearTimeout(timer)
    timer = undefined
    discarded.value = false
    baseline.value = fingerprint(compare(saved))
    measure()
    // Before anything is written: a draft of this record from before has to be read, not overwritten.
    lookForDraft()
    writeOrRemoveDraft()
  }

  function markSaved(state?: unknown) {
    saved = snapshot((state ?? options.state()) as T)
    savedVersion.value = options.version?.() ?? null
    rebase()
  }

  function markSavedWhen(request: Promise<unknown> | null | undefined) {
    // A store that declines a request it may not make answers null instead of a request.
    if (request == null) return Promise.resolve(false)
    return request.then(
      () => {
        markSaved()
        return true
      },
      () => false
    )
  }

  function adopt(fields: Record<string, unknown> | ((_saved: unknown) => unknown)) {
    if (saved === null) return
    if (typeof fields === "function") {
      saved = snapshot(fields(snapshot(saved)) as T)
    } else {
      if (!isPlainObject(saved)) return
      saved = { ...saved, ...snapshot(fields) } as T
    }
    rebase()
  }

  function adoptFiles() {
    if (!options.filesAt) return
    const path = options.filesAt.split(".")
    const at = (state: unknown, keys: string[]) =>
      keys.reduce((value, key) => (isPlainObject(value) ? value[key] : undefined), state)
    // A record's first file creates its list, which reaches the record with the next render.
    nextTick(() => {
      if (saved === null) return
      const onScreen = at(options.state(), path)
      const wasSaved = at(saved, path)
      const holder = at(saved, path.slice(0, -1))
      if (!isPlainObject(holder)) return
      // Which files there are, and in what order, from the screen; each known file keeps its saved tags
      // and due date, so an edit of those still counts.
      holder[path[path.length - 1]] = Array.isArray(onScreen)
        ? onScreen.map(file => (Array.isArray(wasSaved) && wasSaved.find(it => it?.id === file?.id)) || snapshot(file))
        : snapshot(onScreen)
      rebase()
    })
  }

  function forget() {
    clearTimeout(timer)
    timer = undefined
    saved = null
    baseline.value = null
    current.value = null
    discarded.value = false
    pendingDraft.value = null
    draftKey = null
  }

  function discard() {
    clearTimeout(timer)
    timer = undefined
    if (draftKey) removeOwnDraft(draftKey)
    pendingDraft.value = null
    if (baseline.value === null) return
    saved = snapshot(options.state())
    baseline.value = current.value = fingerprint(compare(saved))
    discarded.value = true
  }

  const authStore = useAuthStore()
  const storageKey = computed(() => {
    if (!options.restore) return null
    const key = options.draft?.()
    return key ? draftStorageKey({ tenant: auth.getTenant(), userId: authStore.getId }, key) : null
  })

  /** Once tracking has started, and whenever the record's key changes: is there a draft of it? */
  function lookForDraft() {
    const key = storageKey.value
    if (baseline.value === null || key === draftKey) return
    draftKey = key
    pendingDraft.value = null
    if (!key) return
    const draft = readDraft(key)
    if (!draft) return
    // Nothing in it that is not on screen already: saved since, or never different.
    if (fingerprint(compare(draft.data as T)) === current.value) {
      removeDraft(key)
      return
    }
    pendingDraft.value = draft
  }
  // After a sign-in the person may become known only after the page has loaded its record. That is
  // the case drafts are kept for (the session ran out mid-edit, and the sign-in leads back here), so
  // the key is watched as well as read on `markSaved`.
  watch(storageKey, () => {
    lookForDraft()
    writeOrRemoveDraft()
  })

  function restoreDraft() {
    const draft = pendingDraft.value
    if (!draft) return
    pendingDraft.value = null
    const edits = snapshot(draft.data) as T
    if (isPlainObject(edits) && isPlainObject(draft.base) && isPlainObject(saved)) {
      mergeUnchanged(edits, draft.base, snapshot(saved) as Record<string, unknown>)
    }
    options.restore?.(edits)
    flush()
  }

  function dismissDraft() {
    if (draftKey) removeDraft(draftKey)
    pendingDraft.value = null
    flush()
  }

  function removeDraftOf(key: DraftKey) {
    const other = draftStorageKey({ tenant: auth.getTenant(), userId: authStore.getId }, key)
    if (other && other !== draftKey) removeOwnDraft(other)
  }

  function submit() {
    if (!save) return Promise.resolve(false)
    running ??= Promise.resolve(save())
      .then(result => {
        // What was offered is older than what was just written; restoring it would write it back.
        if (result === true && pendingDraft.value) dismissDraft()
        return result
      })
      .finally(() => (running = null))
    return running
  }

  const entry: UnsavedChangesEntry = {
    label: options.label,
    isDirty: () => isDirty.value,
    flush,
    submit,
    get savesOnLeave() {
      return Boolean(options.savesOnLeave && save)
    },
    discard,
    reveal: options.reveal,
    layer: options.layer ?? "page",
    isFront: () => Boolean(save) && (options.isFront?.() ?? true),
  }
  const unregister = registerUnsavedChanges(entry)

  onScopeDispose(() => {
    // Left without the question (the session ran out): the last edits go into the draft.
    flush()
    unregister()
  })

  const changes: UnsavedChanges = {
    isDirty,
    pendingDraft,
    draftIsOutdated,
    markSaved,
    markSavedWhen,
    adopt,
    adoptFiles,
    forget,
    discard,
    flush,
    restoreDraft,
    dismissDraft,
    removeDraftOf,
    setSubmit: fn => (save = fn),
    submit,
    isSaving: () => running !== null,
    entry,
  }
  provide(UNSAVED_CHANGES, changes)
  return changes
}

/** The tracking of the surface around a component, or null outside one. */
export const useUnsavedChangesContext = () => inject(UNSAVED_CHANGES, null)

/**
 * For the component holding a surface's Speichern button: tells the tracking around it what the button
 * does, and answers what the button should run — the same save, one at a time, as Ctrl+S and the leave
 * question's Speichern — and its variant, which stands back while there is nothing to save.
 */
export function useUnsavedChangesSaveButton(save: () => unknown) {
  const changes = useUnsavedChangesContext()
  changes?.setSubmit(save)
  return {
    save: changes ? changes.submit : save,
    variant: computed(() => (changes && !changes.isDirty.value ? "tonal" : "elevated")),
  }
}

export interface DialogChanges {
  isDirty: ComputedRef<boolean>
  /** Takes values the dialog filled in by itself (a suggestion, a looked-up default) as not the user's. */
  adopt: (_fields: Record<string, unknown>) => void
  /** Asks before the dialog's edits are thrown away. Resolves whether it may close. */
  mayClose: () => Promise<boolean>
}

export interface DialogChangesOptions {
  /**
   * Truthy while the dialog is open. Its starting point is what it shows once its fields have
   * rendered after opening; another truthy value while open (the dialog pointed at another item)
   * starts over.
   */
  open: () => unknown
  /** What the dialog edits, compared like a page's state. */
  state: () => unknown
  /** How the question names it: the dialog's heading. */
  title: () => string
  /** The dialog opened with something not saved yet: an entry the server refused, handed back. */
  unsaved?: () => boolean
}

/**
 * For a dialog that edits something before it is taken over. While it holds changes, Esc and a click
 * beside it do not close it (bind `isDirty` to `persistent`), and its own close button asks first
 * (`mayClose`). No draft and nothing registered: a dialog is a short errand, and the page it sits on
 * has its own tracking.
 */
export function useDialogChanges({ open, state, title, unsaved }: DialogChangesOptions): DialogChanges {
  // What the dialog opened with, and its fingerprint. Null while it is closed.
  const opened = shallowRef<{ state: unknown; fingerprint: string } | null>(null)
  // Read through the reactive proxies rather than `fingerprint`'s raw copy, so that the computed
  // follows every field the dialog edits.
  const measure = () => JSON.stringify(normalize(state(), new WeakSet())) ?? ""
  // Never in the Erklärung's frame: its walkthrough closes the dialogs it filled with Esc.
  const isDirty = computed(
    () =>
      !IS_VIEW_HELP_EMBED && opened.value !== null && (Boolean(unsaved?.()) || measure() !== opened.value.fingerprint)
  )

  function start() {
    if (open()) opened.value = { state: snapshot(state()), fingerprint: measure() }
  }
  // Once the fields have rendered: a field that fills in its default on mount is not an edit.
  watch(
    open,
    isOpen => {
      opened.value = null
      if (isOpen) nextTick(start)
    },
    { immediate: true }
  )

  function adopt(fields: Record<string, unknown>) {
    if (!opened.value || !isPlainObject(opened.value.state)) return
    const next = { ...opened.value.state, ...snapshot(fields) }
    opened.value = { state: next, fingerprint: fingerprint(next) }
  }

  const entry: LeaveQuestionEntry = {
    label: () => ({ icon: "mdi-card-text-outline", name: title() }),
    isDirty: () => isDirty.value,
    discard: () => (opened.value = null),
    savesOnLeave: false,
  }

  return { isDirty, adopt, mayClose: () => confirmLeave([entry]) }
}
